import { useCallback } from 'react'

import { Group, Stack, Tabs } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

import type { InSideType } from '@repo/common/queries/fission_dex'
import type { FundSymbol } from '@repo/common/queries/products'

import { ContentBox } from '@/components/ContentBox'
import { DexCharts } from '@/components/DexCharts'
import { FundReturnMetrics } from '@/components/FundReturnMetrics'
import { FundRow } from '@/components/FundRow'
import { PerShareChart } from '@/components/PerShareChart'
import { PoolStats } from '@/components/PoolStats'
import { FundMint } from '@/flows/FundMint'
import { LimitOrder } from '@/flows/LimitOrder'
import { Swap } from '@/flows/Swap'

// do to TanStack Router auto generate file routes
// prettier-ignore
export const Route = createFileRoute('/_app/trade/')({
  component: DefaultTradePage,
})

function DefaultTradePage() {
  return <TradePage fund_symbol="TOKX" starting_tab="mint" />
}

// eslint-disable-next-line max-lines-per-function
export function TradePage({
  fund_symbol,
  starting_tab,
  limit_order_starting_values = {},
}: {
  fund_symbol: FundSymbol
  starting_tab: 'mint' | 'swap' | 'limit'
  limit_order_starting_values?: {
    starting_in_side?: InSideType
    starting_price?: number
    starting_tick?: number
  }
}) {
  const navigate = useNavigate()
  const onCompleted = useCallback(() => {
    void navigate({ to: '/portfolio' })
  }, [navigate])

  return (
    <>
      <Stack gap="sm">
        <FundRow variant="details" symbol={fund_symbol} />
        <Tabs keepMounted={false} defaultValue={starting_tab} variant="pills">
          <Tabs.List mb="sm">
            <Tabs.Tab value="mint">Mint</Tabs.Tab>
            <Tabs.Tab value="swap">Swap</Tabs.Tab>
            <Tabs.Tab value="limit">Limit Order</Tabs.Tab>
          </Tabs.List>

          <Group
            w="100%"
            mt="lg"
            gap="calc(2*var(--mantine-spacing-xxl))"
            align="flex-start"
          >
            <Stack w="min-content">
              <Tabs.Panel value="mint">
                <FundMint fund_symbol={fund_symbol} onCompleted={onCompleted} />
              </Tabs.Panel>
              <Tabs.Panel value="swap">
                <Swap
                  fund_symbol={fund_symbol}
                  in_side="USDC"
                  onCompleted={onCompleted}
                />
              </Tabs.Panel>
              <Tabs.Panel value="limit">
                <LimitOrder
                  {...limit_order_starting_values}
                  fund_symbol={fund_symbol}
                  onCompleted={onCompleted}
                />
              </Tabs.Panel>
            </Stack>
            <Stack flex="1 1 100vw" maw="44.4rem">
              <Tabs.Panel value="mint">
                <Stack gap="xl">
                  <ContentBox>
                    <PerShareChart symbol={fund_symbol} />
                  </ContentBox>
                  <FundReturnMetrics symbol={fund_symbol} />
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="swap">
                <Stack gap="xl">
                  <DexCharts
                    fund_symbol={fund_symbol}
                    size="lg"
                    starting_type="price"
                  />
                  <PoolStats symbol={fund_symbol} />
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="limit">
                <Stack gap="xl">
                  <DexCharts
                    fund_symbol={fund_symbol}
                    size="lg"
                    starting_type="price"
                  />
                  <PoolStats symbol={fund_symbol} />
                </Stack>
              </Tabs.Panel>
            </Stack>
          </Group>
        </Tabs>
      </Stack>
    </>
  )
}

{
  /* <Group justify="flex-end" w="100%" mt="40px" opacity={0.6}>
<LinkButton
  to="/trade/$symbol/create-position"
  size="sm"
  variant="subtle"
  params={{ symbol }}
>
  Create Manual Position
</LinkButton>
</Group> */
}
